/**
 * SKOP AI Analysis Panel Component
 *
 * Displays AI-powered token analysis.
 * - Teaser: Shown to all users (cached/shared)
 * - Full Analysis: Available to all users
 * - Chat: Available to all users
 */

import { useState, useEffect, useRef } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { type TokenAnalysis } from '../lib/api';
import {
  getAITeaser,
  getFullAIAnalysis,
  sendAIChatMessage,
  getRemainingAIUsage,
  getExistingChatSession,
  clearChatSession,
  getSentimentColor,
  getActionColor,
  getActionLabel,
  type AITeaser,
  type AIFullAnalysis,
  type AIChatMessage,
} from '../lib/ai';

// Sentiment icon component
function SentimentIcon({ sentiment }: { sentiment: AITeaser['sentiment'] }) {
  const color = getSentimentColor(sentiment);

  switch (sentiment) {
    case 'bullish':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      );
    case 'bearish':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
          <polyline points="17 18 23 18 23 12"/>
        </svg>
      );
    case 'warning':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      );
    default:
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
      );
  }
}

interface AIAnalysisPanelProps {
  token: TokenAnalysis;
}

export function AIAnalysisPanel({ token }: AIAnalysisPanelProps) {
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toString() ?? null;

  const [teaser, setTeaser] = useState<AITeaser | null>(null);
  const [fullAnalysis, setFullAnalysis] = useState<AIFullAnalysis | null>(null);
  const [loadingTeaser, setLoadingTeaser] = useState(true);
  const [loadingFull, setLoadingFull] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<AIChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['recommendation']));

  const chatEndRef = useRef<HTMLDivElement>(null);

  const remaining = getRemainingAIUsage(walletAddress);

  // Load teaser on mount
  useEffect(() => {
    setLoadingTeaser(true);
    setTeaser(null);
    setFullAnalysis(null);
    setShowFullAnalysis(false);

    getAITeaser(token)
      .then(setTeaser)
      .catch(() => setError('Failed to load AI analysis'))
      .finally(() => setLoadingTeaser(false));
  }, [token.address]);

  // Load existing chat session
  useEffect(() => {
    if (walletAddress) {
      const session = getExistingChatSession(token.address, walletAddress);
      if (session) {
        setChatMessages(session.messages);
      }
    }
  }, [token.address, walletAddress]);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleGetFullAnalysis = async () => {
    if (!walletAddress) {
      setError('Connect your wallet to access full analysis.');
      return;
    }

    setLoadingFull(true);
    setError(null);

    try {
      const result = await getFullAIAnalysis(token, walletAddress);
      if ('error' in result) {
        setError(result.error);
      } else {
        setFullAnalysis(result);
        setShowFullAnalysis(true);
      }
    } catch {
      setError('Failed to generate analysis');
    } finally {
      setLoadingFull(false);
    }
  };

  const handleSendChat = async () => {
    if (!walletAddress || !chatInput.trim() || sendingChat) return;

    const message = chatInput.trim();
    setChatInput('');
    setSendingChat(true);

    // Add user message immediately
    const userMessage: AIChatMessage = {
      role: 'user',
      content: message,
      timestamp: Date.now(),
    };
    setChatMessages(prev => [...prev, userMessage]);

    try {
      const result = await sendAIChatMessage(token.address, message, walletAddress);
      if ('error' in result) {
        setError(result.error);
      } else {
        const assistantMessage: AIChatMessage = {
          role: 'assistant',
          content: result.response,
          timestamp: Date.now(),
        };
        setChatMessages(prev => [...prev, assistantMessage]);
      }
    } catch {
      setError('Failed to send message');
    } finally {
      setSendingChat(false);
    }
  };

  const handleClearChat = () => {
    if (walletAddress) {
      clearChatSession(token.address, walletAddress);
      setChatMessages([]);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid var(--border)',
        background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.05) 0%, transparent 100%)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>AI</span>
          <span style={{ fontWeight: 600 }}>AI Analysis</span>
          <span style={{
            padding: '2px 6px',
            background: 'rgba(0, 240, 255, 0.1)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--accent)',
            fontSize: '10px',
            fontWeight: 600,
          }}>
            Sonnet
          </span>
        </div>
        {walletAddress && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '11px',
            color: 'var(--text-tertiary)',
          }}>
            <span>
              {remaining.fullAnalysis === -1 ? 'Unlimited' : `${remaining.fullAnalysis} analyses left today`}
            </span>
          </div>
        )}
      </div>

      {/* Teaser Section */}
      <div style={{ padding: '16px' }}>
        {loadingTeaser ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'var(--text-tertiary)',
          }}>
            <div
              className="animate-spin"
              style={{
                width: '14px',
                height: '14px',
                border: '2px solid var(--border)',
                borderTopColor: 'var(--accent)',
                borderRadius: '50%',
              }}
            />
            <span>Analyzing token...</span>
          </div>
        ) : teaser ? (
          <div>
            {/* Sentiment Badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
            }}>
              <SentimentIcon sentiment={teaser.sentiment} />
              <span style={{
                padding: '4px 10px',
                background: `${getSentimentColor(teaser.sentiment)}20`,
                borderRadius: 'var(--radius-md)',
                color: getSentimentColor(teaser.sentiment),
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'capitalize',
              }}>
                {teaser.sentiment}
              </span>
              <span style={{
                padding: '4px 8px',
                background: 'var(--bg-tertiary)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-tertiary)',
                fontSize: '10px',
              }}>
                {teaser.confidence} confidence
              </span>
            </div>

            {/* Summary */}
            <p style={{
              fontSize: '14px',
              color: 'var(--text-primary)',
              lineHeight: 1.5,
              margin: 0,
            }}>
              {teaser.summary}
            </p>

            {/* Actions */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginTop: '16px',
              flexWrap: 'wrap',
            }}>
              {!fullAnalysis && (
                <button
                  onClick={handleGetFullAnalysis}
                  disabled={loadingFull}
                  style={{
                    padding: '10px 20px',
                    background: 'var(--accent)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    color: '#000',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: loadingFull ? 'not-allowed' : 'pointer',
                    opacity: loadingFull ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  {loadingFull ? (
                    <>
                      <div
                        className="animate-spin"
                        style={{
                          width: '12px',
                          height: '12px',
                          border: '2px solid rgba(0, 0, 0, 0.2)',
                          borderTopColor: '#000',
                          borderRadius: '50%',
                        }}
                      />
                      Analyzing...
                    </>
                  ) : (
                    'Get Full Analysis'
                  )}
                </button>
              )}

              {fullAnalysis && (
                <button
                  onClick={() => setShowFullAnalysis(!showFullAnalysis)}
                  style={{
                    padding: '10px 20px',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  {showFullAnalysis ? 'Hide Full Analysis' : 'Show Full Analysis'}
                </button>
              )}

              {remaining.chatMessages !== 0 && (
                <button
                  onClick={() => setShowChat(!showChat)}
                  style={{
                    padding: '10px 20px',
                    background: showChat ? 'var(--accent)' : 'var(--bg-tertiary)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    color: showChat ? '#000' : 'var(--text-primary)',
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  Ask AI
                  {remaining.chatMessages !== -1 && (
                    <span style={{
                      padding: '2px 6px',
                      background: showChat ? 'rgba(0,0,0,0.2)' : 'var(--bg-secondary)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '10px',
                    }}>
                      {remaining.chatMessages}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        ) : null}

        {/* Error */}
        {error && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            background: 'rgba(255, 59, 59, 0.1)',
            border: '1px solid rgba(255, 59, 59, 0.2)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--error)',
            fontSize: '13px',
          }}>
            {error}
          </div>
        )}
      </div>

      {/* Full Analysis Section */}
      {showFullAnalysis && fullAnalysis && (
        <div style={{
          borderTop: '1px solid var(--border)',
          padding: '16px',
        }}>
          {/* Summary */}
          <p style={{
            fontSize: '14px',
            color: 'var(--text-primary)',
            lineHeight: 1.5,
            marginBottom: '16px',
          }}>
            {fullAnalysis.summary}
          </p>

          {/* Recommendation */}
          <ExpandableSection
            title="Recommendation"
            expanded={expandedSections.has('recommendation')}
            onToggle={() => toggleSection('recommendation')}
            badge={{
              label: getActionLabel(fullAnalysis.recommendation.action),
              color: getActionColor(fullAnalysis.recommendation.action),
            }}
          >
            <p style={{ margin: '0 0 12px 0', lineHeight: 1.5 }}>
              {fullAnalysis.recommendation.reasoning}
            </p>
            {fullAnalysis.recommendation.suggestedPosition && (
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <InfoChip label="Position Size" value={fullAnalysis.recommendation.suggestedPosition} />
                {fullAnalysis.recommendation.stopLoss && (
                  <InfoChip label="Stop Loss" value={fullAnalysis.recommendation.stopLoss} />
                )}
              </div>
            )}
          </ExpandableSection>

          {/* Risk Assessment */}
          <ExpandableSection
            title="Risk Assessment"
            expanded={expandedSections.has('risk')}
            onToggle={() => toggleSection('risk')}
            badge={{
              label: `${fullAnalysis.riskAssessment.score}/100`,
              color: fullAnalysis.riskAssessment.score >= 50 ? '#ff3b3b' : fullAnalysis.riskAssessment.score >= 25 ? '#ffb800' : '#00ff88',
            }}
          >
            <p style={{ margin: '0 0 12px 0', lineHeight: 1.5 }}>
              {fullAnalysis.riskAssessment.reasoning}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {fullAnalysis.riskAssessment.keyFactors.map((factor, i) => (
                <span key={i} style={{
                  padding: '4px 8px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '11px',
                  color: 'var(--text-secondary)',
                }}>
                  {factor}
                </span>
              ))}
            </div>
          </ExpandableSection>

          {/* Creator Analysis */}
          <ExpandableSection
            title="Creator Analysis"
            expanded={expandedSections.has('creator')}
            onToggle={() => toggleSection('creator')}
            badge={{
              label: fullAnalysis.creatorAnalysis.trustLevel,
              color: fullAnalysis.creatorAnalysis.trustLevel === 'trusted' ? '#00ff88' :
                     fullAnalysis.creatorAnalysis.trustLevel === 'dangerous' ? '#ff3b3b' :
                     fullAnalysis.creatorAnalysis.trustLevel === 'suspicious' ? '#ff8800' : '#888',
            }}
          >
            <p style={{ margin: '0 0 12px 0', lineHeight: 1.5 }}>
              {fullAnalysis.creatorAnalysis.reasoning}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>
                  Red Flags
                </div>
                {fullAnalysis.creatorAnalysis.redFlags.length > 0 ? (
                  fullAnalysis.creatorAnalysis.redFlags.map((flag, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '12px',
                      color: 'var(--error)',
                      marginBottom: '4px',
                    }}>
                      <span>X</span>
                      {flag}
                    </div>
                  ))
                ) : (
                  <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>None detected</span>
                )}
              </div>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>
                  Green Flags
                </div>
                {fullAnalysis.creatorAnalysis.greenFlags.length > 0 ? (
                  fullAnalysis.creatorAnalysis.greenFlags.map((flag, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '12px',
                      color: 'var(--success)',
                      marginBottom: '4px',
                    }}>
                      <span>V</span>
                      {flag}
                    </div>
                  ))
                ) : (
                  <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>None detected</span>
                )}
              </div>
            </div>
          </ExpandableSection>

          {/* Market Analysis */}
          <ExpandableSection
            title="Market Analysis"
            expanded={expandedSections.has('market')}
            onToggle={() => toggleSection('market')}
          >
            <p style={{ margin: '0 0 12px 0', lineHeight: 1.5 }}>
              {fullAnalysis.marketAnalysis.reasoning}
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <InfoChip
                label="Volume"
                value={fullAnalysis.marketAnalysis.volumeAuthenticity}
                color={fullAnalysis.marketAnalysis.volumeAuthenticity === 'organic' ? '#00ff88' :
                       fullAnalysis.marketAnalysis.volumeAuthenticity === 'suspicious' ? '#ff3b3b' : '#ffb800'}
              />
              <InfoChip
                label="Holders"
                value={fullAnalysis.marketAnalysis.holderDistribution}
                color={fullAnalysis.marketAnalysis.holderDistribution === 'healthy' ? '#00ff88' :
                       fullAnalysis.marketAnalysis.holderDistribution === 'concerning' ? '#ff3b3b' : '#ffb800'}
              />
            </div>
          </ExpandableSection>

          {/* Additional Insights */}
          <ExpandableSection
            title="Additional Insights"
            expanded={expandedSections.has('insights')}
            onToggle={() => toggleSection('insights')}
          >
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {fullAnalysis.additionalInsights.map((insight, i) => (
                <li key={i} style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px',
                  lineHeight: 1.4,
                }}>
                  {insight}
                </li>
              ))}
            </ul>
          </ExpandableSection>

          {/* Generated timestamp */}
          <div style={{
            marginTop: '16px',
            fontSize: '11px',
            color: 'var(--text-tertiary)',
            textAlign: 'center',
          }}>
            Generated {new Date(fullAnalysis.generatedAt).toLocaleString()}
          </div>
        </div>
      )}

      {/* Chat Section */}
      {showChat && (
        <div style={{
          borderTop: '1px solid var(--border)',
          background: 'var(--bg-tertiary)',
        }}>
          {/* Chat Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 16px',
            borderBottom: '1px solid var(--border)',
          }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Ask follow-up questions about this token
            </span>
            {chatMessages.length > 0 && (
              <button
                onClick={handleClearChat}
                style={{
                  padding: '4px 8px',
                  background: 'none',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-tertiary)',
                  fontSize: '10px',
                  cursor: 'pointer',
                }}
              >
                Clear Chat
              </button>
            )}
          </div>

          {/* Messages */}
          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            padding: '12px 16px',
          }}>
            {chatMessages.length === 0 ? (
              <div style={{
                textAlign: 'center',
                color: 'var(--text-tertiary)',
                fontSize: '13px',
                padding: '20px 0',
              }}>
                Ask anything about {token.symbol}
              </div>
            ) : (
              chatMessages.map((msg, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: '12px',
                }}>
                  <div style={{
                    maxWidth: '80%',
                    padding: '10px 14px',
                    background: msg.role === 'user' ? 'var(--accent)' : 'var(--bg-secondary)',
                    color: msg.role === 'user' ? '#000' : 'var(--text-primary)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '13px',
                    lineHeight: 1.4,
                  }}>
                    {msg.content}
                  </div>
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div style={{
            display: 'flex',
            gap: '8px',
            padding: '12px 16px',
            borderTop: '1px solid var(--border)',
          }}>
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
              placeholder="Ask a question..."
              disabled={sendingChat || remaining.chatMessages === 0}
              style={{
                flex: 1,
                padding: '10px 14px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--text-primary)',
                fontSize: '13px',
                outline: 'none',
              }}
            />
            <button
              onClick={handleSendChat}
              disabled={sendingChat || !chatInput.trim() || remaining.chatMessages === 0}
              style={{
                padding: '10px 16px',
                background: 'var(--accent)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                color: '#000',
                fontWeight: 600,
                cursor: sendingChat || !chatInput.trim() ? 'not-allowed' : 'pointer',
                opacity: sendingChat || !chatInput.trim() ? 0.7 : 1,
              }}
            >
              {sendingChat ? '...' : 'Send'}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function ExpandableSection({
  title,
  expanded,
  onToggle,
  badge,
  children,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  badge?: { label: string; color: string };
  children: React.ReactNode;
}) {
  return (
    <div style={{
      marginBottom: '8px',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
      overflow: 'hidden',
    }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: 'var(--bg-tertiary)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '13px' }}>
            {title}
          </span>
          {badge && (
            <span style={{
              padding: '2px 8px',
              background: `${badge.color}20`,
              borderRadius: 'var(--radius-sm)',
              color: badge.color,
              fontSize: '10px',
              fontWeight: 600,
              textTransform: 'capitalize',
            }}>
              {badge.label}
            </span>
          )}
        </div>
        <span style={{
          color: 'var(--text-tertiary)',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }}>
          V
        </span>
      </button>
      {expanded && (
        <div style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-secondary)' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function InfoChip({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{label}</span>
      <span style={{
        fontSize: '12px',
        fontWeight: 600,
        color: color || 'var(--text-primary)',
        textTransform: 'capitalize',
      }}>
        {value}
      </span>
    </div>
  );
}
